import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { ReviewStatusBadge, ReviewBadgeStatus } from '@/components/ui/ReviewStatusBadge';
import { TrainingVideo } from '@/types/videoAnnotations';
import { useI18n } from '@/contexts/I18nContext';
import { SOSSIETY_RED, REVIEW_COLOR_PENDING } from '@/styles/brand';
import { isAcceptanceExpired } from '../utils/reviewWindows';

export type ReviewStatus = 'pending' | 'in_review' | 'delivered' | 'expired' | 'declined' | 'cancelled';

export const getReviewStatus = (video: TrainingVideo): ReviewStatus => {
  if (video.status === 'pending_review' && isAcceptanceExpired(video.created_at)) return 'expired';
  if (video.status === 'pending_review') return 'pending';
  if (video.status === 'under_review') return 'in_review';
  if (video.status === 'reviewed') return 'delivered';
  if (video.status === 'declined') return 'declined';
  if (video.status === 'archived') return 'cancelled';
  return 'pending';
};

const parseBeltLabel = (beltRank?: string, t?: any): string => {
  const rank = beltRank?.toLowerCase() ?? '';
  if (!t) return 'Black Belt';
  if (rank.includes('black'))  return t('roll_review.black_belt');
  if (rank.includes('brown'))  return t('roll_review.brown_belt');
  if (rank.includes('purple')) return t('roll_review.purple_belt');
  if (rank.includes('blue'))   return t('roll_review.blue_belt');
  return t('roll_review.white_belt');
};

interface ReviewRequestCardProps {
  video: TrainingVideo;
}

export const ReviewRequestCard: React.FC<ReviewRequestCardProps> = ({ video }) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const status = getReviewStatus(video);

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/cancel-review/${video.id}`);
  };

  const instructor = video.reviewer;
  const instructorName = instructor?.full_name ?? t('my_reviews.unknown_instructor');
  const instructorInitials = instructorName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const beltLabel = parseBeltLabel(instructor?.belt_rank, t);
  const submittedDate = format(new Date(video.created_at), 'MMM d, yyyy');

  const excerpt = video.description
    ? `"${video.description.length > 55 ? video.description.slice(0, 55) + '…' : video.description}"`
    : null;

  // Status label map
  const statusLabel: Record<ReviewStatus, string> = {
    pending:   t('my_reviews.status_pending'),
    in_review: t('my_reviews.status_in_review'),
    delivered: t('my_reviews.status_delivered'),
    expired:   t('my_reviews.status_expired'),
    declined:  t('my_reviews.status_declined'),
    cancelled: t('my_reviews.status_cancelled'),
  };

  return (
    <button
      onClick={() => navigate(`/video-review/${video.id}`)}
      className="w-full text-left bg-sossiety-surface hover:bg-sossiety-overlay active:bg-sossiety-overlay/80 transition-colors rounded-2xl p-4 flex flex-col gap-3 border border-white/5"
    >
      {/* Top row: avatar + name + belt + date + status badge */}
      <div className="flex items-center gap-3">
        <FramedAvatar
          src={instructor?.profile_picture_url}
          alt={instructorName}
          initials={instructorInitials}
          size={36}
          frameSize={{ w: 48, h: 52 }}
          beltRank="black"
          isManual
        />
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-bold leading-tight truncate">{instructorName}</p>
          <p className="text-white/40 text-xs mt-0.5">
            {beltLabel} · {submittedDate}
          </p>
        </div>
        <ReviewStatusBadge status={status as ReviewBadgeStatus} label={statusLabel[status]} />
      </div>

      {/* Message excerpt — only shown when practitioner left a message */}
      {excerpt && (
        <p className="text-white/70 text-sm leading-snug line-clamp-2">{excerpt}</p>
      )}

      {/* Bottom row: video duration + contextual info */}
      <StatusFooter status={status} video={video} t={t} onCancel={handleCancelClick} />
    </button>
  );
};

/* ── Contextual footer per status ───────────────────────────────────── */
const StatusFooter: React.FC<{
  status: ReviewStatus;
  video: TrainingVideo;
  t: any;
  onCancel: (e: React.MouseEvent) => void;
}> = ({ status, video, t, onCancel }) => {
  // Video duration — stored in metadata or fall back to null
  const duration: string | null = (video as any).duration_label ?? null;

  const durationEl = duration ? (
    <span className="text-white/40 text-xs">{duration}</span>
  ) : null;

  if (status === 'delivered') {
    const commentCount: number = (video as any).annotation_count ?? 0;
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {durationEl}
          {commentCount > 0 && (
            <span className="text-white/40 text-xs">
              {t('my_reviews.comments_count', { count: commentCount })}
            </span>
          )}
        </div>
        <span className="text-xs font-semibold" style={{ color: SOSSIETY_RED }}>
          {t('my_reviews.view_feedback')}
        </span>
      </div>
    );
  }

  if (status === 'in_review') {
    return (
      <div className="flex items-center gap-3">
        {durationEl}
        <span className="text-blue-400 text-xs">{t('my_reviews.remaining_48h')}</span>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {durationEl}
          <span className="text-xs" style={{ color: REVIEW_COLOR_PENDING }}>{t('my_reviews.waiting_acceptance')}</span>
        </div>
        <button
          onClick={onCancel}
          className="text-xs text-white/30 hover:text-white/60 transition-colors"
        >
          {t('my_reviews.cancel_request')}
        </button>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="flex items-center gap-3">
        {durationEl}
        <span className="text-white/30 text-xs">{t('my_reviews.no_response_48h')}</span>
      </div>
    );
  }

  return null;
};
